import { encodeCoachId } from './hashids';
import {
  AUTH_EVENT_TYPES,
  clearAuthenticatedSession,
  getAccessTokenForApiHeaders,
  getStoredUser,
  hasAuthenticatedSession,
  isStoredAccessTokenValid,
  publishAuthEvent,
  refreshSessionIfNeeded,
  requestBackendLogout,
  setAuthenticatedSession,
} from './authSession';
import { getPremiumLandingPath } from './subscriptionProductScope';
import {
  isLearnerAppBlockedUser,
  learnerAppAccessDeniedMessage,
} from './staffAppAccess';
import {
  resolveApiBaseUrl,
  resolveApiSocketUrl,
  resolveGoogleClientId,
} from './apiBaseUrl';
import { extractPronounceLemma } from './pronounceLemma';

export const API_BASE_URL = resolveApiBaseUrl();
export const API_SOCKET_URL = resolveApiSocketUrl();
export const GOOGLE_CLIENT_ID = resolveGoogleClientId();
export const ELEVEN_LABS_SPEAKING_AGENT_ID =
  process.env.REACT_APP_ELEVEN_LABS_SPEAKING_AGENT_ID;
export const ELEVEN_LABS_INTERVIEW_AGENT_ID =
  process.env.REACT_APP_ELEVEN_LABS_INTERVIEW_AGENT_ID;

export const LOGIN_REDIRECT_MESSAGE_STORAGE_KEY = 'login_redirect_message';
export const DEFAULT_SESSION_EXPIRED_LOGIN_MESSAGE =
  'Votre session a expire. Veuillez vous reconnecter.';
export const DEFAULT_SESSION_EXPIRED_TOAST_MESSAGE =
  'Votre session a expire. Redirection vers la connexion...';

let authRedirectInFlight = false;
let authRedirectTimeoutId = null;

// API functions to interact with backend
export const getAuthHeaders = () => {
  const headers = {
    'Content-Type': 'application/json',
  };
  const token = getAccessTokenForApiHeaders();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

export const normalizeStorageUserEmail = (email) =>
  encodeURIComponent(String(email || '').trim().toLowerCase());

export const getCurrentUserEmailForStorage = () => {
  const user = getStoredUser();
  if (user?.email) return user.email;

  try {
    return localStorage.getItem('userEmail') || null;
  } catch {
    return null;
  }
};

export const getAccountScopedLocalStorageKey = (
  key,
  userEmail = getCurrentUserEmailForStorage()
) => {
  if (!key || !userEmail) return null;
  return `${key}_${normalizeStorageUserEmail(userEmail)}`;
};

// Function to clear all caches when user logs out or token becomes invalid
export const clearAllCaches = ({ preserveTabState = false } = {}) => {
  clearAuthenticatedSession({ preserveTabState });
  localStorage.removeItem('gre_vocabulary_enrollment');

  Object.keys(localStorage).forEach((key) => {
    if (
      key.startsWith('article_') ||
      key.startsWith('article_exercises_') ||
      key.startsWith('gre_article_cache_') ||
      key.startsWith('gre_article_exercises_cache_')
    ) {
      localStorage.removeItem(key);
    }
  });

  // Clear sessionStorage vocabulary cache
  const vocabCacheKeys = [
    'gre_vocab_enrollment_session',
    'gre_vocab_daily_articles_session',
    'gre_vocab_cache_timestamp',
  ];

  vocabCacheKeys.forEach((key) => {
    sessionStorage.removeItem(key);
    sessionStorage.removeItem(`${key}_timestamp`);
  });
};

// Function to check if token is valid (can be used to detect 401 responses).
// NOTE: This is a pure check. It must not have side effects like clearing
// caches. Clearing the auth session as a side effect previously caused a
// race where a freshly-issued session could be wiped out by an unrelated
// caller invoking this check, leading to a redirect loop after login.
export const isTokenValid = () => {
  return hasAuthenticatedSession() && isStoredAccessTokenValid();
};

export const toastdisplay = (message, success, duration, setToast) => {
  setToast({ show: true, message, success });
  setTimeout(
    () => setToast({ show: false, message: '', success: true }),
    duration
  );
};

const getCurrentPathWithSearch = () => {
  if (typeof window === 'undefined') {
    return '/login';
  }

  return `${window.location.pathname || ''}${window.location.search || ''}`;
};

export const getStoredLoginRedirectMessage = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  return sessionStorage.getItem(LOGIN_REDIRECT_MESSAGE_STORAGE_KEY);
};

export const consumeStoredLoginRedirectMessage = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const message = sessionStorage.getItem(LOGIN_REDIRECT_MESSAGE_STORAGE_KEY);
  if (message) {
    sessionStorage.removeItem(LOGIN_REDIRECT_MESSAGE_STORAGE_KEY);
  }
  return message;
};

const POST_LOGIN_REDIRECT_KEY = 'redirect_after_login';

const isMeaningfulRedirectPath = (value) => {
  if (typeof value !== 'string') return false;
  if (!value || value === '/' || value === '/login' || value === '/signup') {
    return false;
  }
  return value.startsWith('/');
};

export const getStoredPostLoginRedirect = () => {
  if (typeof window === 'undefined') return null;
  const stored = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
  return isMeaningfulRedirectPath(stored) ? stored : null;
};

export const consumeStoredPostLoginRedirect = () => {
  if (typeof window === 'undefined') return null;
  const stored = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
  if (stored) {
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
  }
  return isMeaningfulRedirectPath(stored) ? stored : null;
};

export const redirectToLoginWithReturn = ({
  navigate,
  redirectTo,
  message = DEFAULT_SESSION_EXPIRED_LOGIN_MESSAGE,
  toastMessage = DEFAULT_SESSION_EXPIRED_TOAST_MESSAGE,
  toastDurationMs = 1500,
  setToast,
  publishLogoutReason,
  clearCachesFirst = true,
  replace = true,
} = {}) => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (authRedirectInFlight) {
    return true;
  }

  authRedirectInFlight = true;
  const candidatePath = redirectTo || getCurrentPathWithSearch();
  const existingStored = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
  // Prefer an existing meaningful redirect target (e.g. an article deep link
  // captured by an earlier guard) so we never overwrite it with /login or /.
  const targetPath = isMeaningfulRedirectPath(candidatePath)
    ? candidatePath
    : isMeaningfulRedirectPath(existingStored)
      ? existingStored
      : candidatePath;

  if (isMeaningfulRedirectPath(targetPath)) {
    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, targetPath);
  }
  sessionStorage.setItem(LOGIN_REDIRECT_MESSAGE_STORAGE_KEY, message);

  if (clearCachesFirst) {
    clearAllCaches();
  }

  if (publishLogoutReason) {
    publishAuthEvent(AUTH_EVENT_TYPES.logout, { reason: publishLogoutReason });
  }

  if (setToast) {
    toastdisplay(toastMessage, false, toastDurationMs, setToast);
  }

  const performRedirect = () => {
    if (navigate) {
      navigate('/login', {
        replace,
        state: {
          redirectTo: targetPath,
          message,
        },
      });
      return;
    }

    window.location.href = '/login';
  };

  if (authRedirectTimeoutId) {
    window.clearTimeout(authRedirectTimeoutId);
  }

  if (setToast && toastDurationMs > 0) {
    authRedirectTimeoutId = window.setTimeout(performRedirect, toastDurationMs);
  } else {
    performRedirect();
  }

  return true;
};

// Generate a fallback avatar URL using DiceBear with a random or user-specific seed
export const generateAvatarUrl = (userIdentifier = null) => {
  // Use user identifier (like email or ID) as seed for consistency, or generate random seed
  const seed = userIdentifier || Math.random().toString(36).substring(2, 15);
  return `https://api.dicebear.com/9.x/glass/svg?seed=${encodeURIComponent(seed)}`;
};

// Get profile picture with fallback to generated avatar
export const getProfilePicture = (user) => {
  // Check if user has a profile picture or avatar
  if (user?.profile_picture) return user.profile_picture;
  if (user?.avatar) return user.avatar;

  // Generate fallback avatar using user email as seed for consistency
  return generateAvatarUrl(user?.email || user?.id);
};

// Get dice avatar URL as fallback for failed profile picture loads
export const getDiceAvatarUrl = (user) => {
  return generateAvatarUrl(user?.email || user?.id || 'default');
};

// Helper function to group conversations by user
export const groupConversationsByUser = (conversations) => {
  const userConversations = new Map();

  conversations.forEach((conv) => {
    // Determine the key for grouping (use email if available, otherwise name)
    const userKey = conv.email || conv.receiver_email || conv.name;

    if (userKey) {
      if (userConversations.has(userKey)) {
        // Merge with existing conversation
        const existing = userConversations.get(userKey);

        // Merge all messages, normalize them, and sort them chronologically
        const allMessages = [...existing.messages, ...conv.messages].map(
          normalizeMessageData
        );
        const sortedMessages = sortMessagesChronologically(allMessages);

        // Find the most recent message for lastMessage display
        const mostRecentMessage = sortedMessages[sortedMessages.length - 1];
        const lastMessage =
          mostRecentMessage?.text ||
          mostRecentMessage?.content ||
          mostRecentMessage?.message ||
          '';

        // Determine the most recent date
        const mostRecentDate =
          sortedMessages.length > 0
            ? sortedMessages[sortedMessages.length - 1].date ||
              sortedMessages[sortedMessages.length - 1].timestamp
            : conv.date > existing.date
              ? conv.date
              : existing.date;

        const merged = {
          ...existing,
          // Use the actual most recent message content
          lastMessage: lastMessage,
          // Keep the most recent date
          date: mostRecentDate,
          // Use the sorted and merged messages
          messages: sortedMessages,
          // Merge any other relevant fields
          request_id: conv.request_id || existing.request_id,
          receiver_email: conv.receiver_email || existing.receiver_email,
          avatar: conv.avatar || existing.avatar,
          // Keep the first ID as the main conversation ID
          id: existing.id,
        };
        userConversations.set(userKey, merged);
      } else {
        // First conversation for this user - normalize messages
        const normalizedConv = {
          ...conv,
          messages: conv.messages?.map(normalizeMessageData) || [],
        };
        userConversations.set(userKey, normalizedConv);
      }
    }
  });

  // Convert map back to array and sort by most recent date
  return Array.from(userConversations.values()).sort(
    (a, b) => new Date(b.date || 0) - new Date(a.date || 0)
  );
};

// Fetch conversations with automatic grouping
export const fetchConversations = async (userEmail) => {
  try {
    const res = await fetch(`${API_BASE_URL}/api/conversations`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) {
      if (res.status === 401) {
        throw new Error('Authentication failed');
      }
      throw new Error('Failed to load conversations');
    }
    const data = await res.json();

    if (Array.isArray(data)) {
      // Group conversations by user to consolidate multiple conversation flows
      return groupConversationsByUser(data);
    } else {
      return [];
    }
  } catch (err) {
    console.error('Error fetching conversations:', err);
    throw err;
  }
};

// Helper function to convert time from sender's timezone to local timezone
export const convertToLocalTime = (dateString, senderTimezone = 'UTC') => {
  try {
    if (!dateString) return '';

    // Parse the date string
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    // Get local timezone
    const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // If sender timezone is UTC or same as local, just format the time
    if (senderTimezone === 'UTC' || senderTimezone === localTimezone) {
      return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
    }

    // For different timezones, we need to handle the conversion properly
    // The dateString should already be in UTC from the server
    // We just need to format it in the local timezone
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: localTimezone,
    });
  } catch (error) {
    console.warn('Error converting timezone:', error);
    // Fallback to original date string
    return dateString;
  }
};

// Helper function to normalize message data from different sources
export const normalizeMessageData = (message) => {
  // Handle messages from backend API
  if (message.created_at && !message.date) {
    const displayTime = convertToLocalTime(
      message.created_at,
      message.sender_timezone
    );
    return {
      ...message,
      date: message.created_at,
      displayTime: displayTime,
      author: message.sender?.name || message.author || 'Unknown',
      text: message.content || message.text || message.message || '',
      serverTime: message.created_at, // Use created_at as server time
      senderTimezone: message.sender_timezone || 'UTC',
    };
  }

  // Handle messages from WebSocket
  if (message.serverTime && !message.server_time) {
    return {
      ...message,
      serverTime: message.server_time,
    };
  }

  // Return as-is if already normalized
  return message;
};

// Wrapper function to ensure conversations are always grouped when updated
export const createGroupedConversationsSetter = (setConversations) => {
  return (newConversations) => {
    if (Array.isArray(newConversations)) {
      const grouped = groupConversationsByUser(newConversations);
      setConversations(grouped);
    } else {
      setConversations([]);
    }
  };
};

// Helper function to sort messages chronologically by date and time
export const sortMessagesChronologically = (messages) => {
  return messages.sort((a, b) => {
    // Parse dates from different possible formats with timezone awareness
    let dateA, dateB;

    // Priority 1: Use server time if available (most accurate)
    if (
      a.serverTime &&
      typeof a.serverTime === 'string' &&
      a.serverTime.includes('T')
    ) {
      dateA = new Date(a.serverTime);
    } else if (a.date && typeof a.date === 'string' && a.date.includes('T')) {
      dateA = new Date(a.date);
    } else if (
      a.timestamp &&
      typeof a.timestamp === 'string' &&
      a.timestamp.includes('T')
    ) {
      dateA = new Date(a.timestamp);
    } else if (a.date && typeof a.date === 'string') {
      // Handle time-only strings like "18:48", "17:04"
      // For time-only strings, we need to determine the actual date
      // This is tricky without context, so we'll use a heuristic
      const now = new Date();
      const [hours, minutes] = a.date.split(':').map(Number);

      // If the time is in the future relative to now, assume it's from yesterday
      // If it's in the past relative to now, assume it's from today
      const messageTime = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        hours,
        minutes
      );
      if (messageTime > now) {
        // Time is in the future, so it must be from yesterday
        messageTime.setDate(messageTime.getDate() - 1);
      }
      dateA = messageTime;
    } else {
      dateA = new Date(0); // Fallback to epoch
    }

    if (
      b.serverTime &&
      typeof b.serverTime === 'string' &&
      b.serverTime.includes('T')
    ) {
      dateB = new Date(b.serverTime);
    } else if (b.date && typeof b.date === 'string' && b.date.includes('T')) {
      dateB = new Date(b.date);
    } else if (
      b.timestamp &&
      typeof b.timestamp === 'string' &&
      b.timestamp.includes('T')
    ) {
      dateB = new Date(b.timestamp);
    } else if (b.date && typeof b.date === 'string') {
      // Handle time-only strings like "18:48", "17:04"
      const now = new Date();
      const [hours, minutes] = b.date.split(':').map(Number);

      const messageTime = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        hours,
        minutes
      );
      if (messageTime > now) {
        // Time is in the future, so it must be from yesterday
        messageTime.setDate(messageTime.getDate() - 1);
      }
      dateB = messageTime;
    } else {
      dateB = new Date(0); // Fallback to epoch
    }

    // Sort from oldest to newest
    return dateA - dateB;
  });
};

// Function to update conversations with new message and ensure proper grouping
export const updateConversationWithMessage = (
  conversations,
  newMessage,
  senderEmail,
  senderName
) => {
  console.log('Conversations:', conversations);
  console.log('New message:', newMessage);
  console.log('Sender email:', senderEmail);
  console.log('Sender name:', senderName);
  // Find existing conversation for this user
  const existingConversation = conversations.find(
    (conv) =>
      conv.email === senderEmail ||
      conv.receiver_email === senderEmail ||
      conv.name === senderName ||
      // Check if any messages in the conversation are from this user
      (conv.messages &&
        conv.messages.some(
          (msg) =>
            msg.author === senderName ||
            msg.sender_email === senderEmail ||
            msg.email === senderEmail
        ))
  );
  console.log('Existing conversation:', existingConversation);

  if (existingConversation) {
    // Update existing conversation with proper chronological sorting
    return conversations.map((conv) =>
      conv.id === existingConversation.id
        ? {
            ...conv,
            messages: sortMessagesChronologically([
              ...conv.messages,
              normalizeMessageData(newMessage),
            ]),
            lastMessage:
              newMessage.text || newMessage.content || newMessage.message || '',
            date:
              newMessage.date ||
              newMessage.timestamp ||
              new Date().toISOString(),
          }
        : conv
    );
  } else {
    // Create new conversation
    const newConversation = {
      id: `temp_${Date.now()}`,
      name: senderName,
      email: senderEmail,
      receiver_email: senderEmail,
      avatar: null,
      status: 'Online',
      messages: [normalizeMessageData(newMessage)],
      lastMessage:
        newMessage.text || newMessage.content || newMessage.message || '',
      date: newMessage.date || newMessage.timestamp || new Date().toISOString(),
      request_id: null,
    };
    return [newConversation, ...conversations];
  }
};

// Handle profile picture loading errors and log them for debugging
export const handleProfilePictureError = (error, user, context = '') => {
  const userInfo = user?.email || user?.id || 'unknown';
  const errorMessage = `Profile picture failed to load for user: ${userInfo}`;

  if (context) {
    console.warn(`${errorMessage} in ${context}`, error);
  } else {
    console.warn(errorMessage, error);
  }

  // Log specific error types for debugging
  if (error.target && error.target.src) {
    console.warn(`Failed URL: ${error.target.src}`);
  }

  // You could also send this to an error reporting service here
  // Example: ErrorReporting.log(errorMessage, { user, context, error });
};

export const checkTokenExpiration = async (
  response,
  setSessionExpired = () => {},
  setToast
) => {
  if (response.status === 401) {
    const refreshResult = await refreshSessionIfNeeded({
      force: true,
      broadcastOnSuccess: true,
    });
    if (refreshResult?.ok) {
      // Session was refreshed successfully. Signal callers that the original
      // request should be retried with the new token rather than treating it
      // as an expired session.
      return 'refreshed';
    }

    setSessionExpired(true);
    redirectToLoginWithReturn({
      setToast,
      publishLogoutReason: 'session_expired',
    });
    return true;
  }
  return false;
};

// Helper function to store user session data
export const storeUserSession = async (user, token) => {
  if (isLearnerAppBlockedUser(user)) {
    clearAuthenticatedSession();
    throw new Error(learnerAppAccessDeniedMessage());
  }

  const currentUserEmail = localStorage.getItem('userEmail');
  setAuthenticatedSession(user, token);

  // Check if the user is the same as data already in the local storage
  if (currentUserEmail !== user.email) {
    // Different user - clear syncable localStorage and load from backend
    console.log(
      `User changed from ${currentUserEmail} to ${user.email} - syncing data`
    );

    // Preserve non-syncable items before clearing
    const pendingCoachAction = localStorage.getItem('pendingCoachAction');
    const fromCoachProfile = localStorage.getItem('fromCoachProfile');
    const liwazaCookieConsent = localStorage.getItem('liwazaCookieConsent');

    // Clear only syncable localStorage data
    clearSyncableLocalStorage();

    // Restore preserved items
    if (pendingCoachAction) {
      localStorage.setItem('pendingCoachAction', pendingCoachAction);
    }
    if (fromCoachProfile) {
      localStorage.setItem('fromCoachProfile', fromCoachProfile);
    }
    if (liwazaCookieConsent) {
      localStorage.setItem('liwazaCookieConsent', liwazaCookieConsent);
    }

    localStorage.setItem('userEmail', user.email);

    // Load user's data from backend
    await loadBackendConfigurationToLocalStorage(user.email, true);
  } else {
    // Same user - ALWAYS load from backend first, then merge intelligently
    console.log(
      `Same user ${user.email} - loading backend data and merging with local`
    );

    // CRITICAL: Always load backend data first to ensure we don't lose progress
    // This handles the case where user logs in on a new machine with stale/empty localStorage
    await loadBackendConfigurationToLocalStorage(user.email, true); // ALWAYS merge with backend (prefer more complete data)

    // Now sync to backend - the protection in syncLocalStorageToBackend will prevent
    // any remaining stale data from overwriting better backend data
    console.log(`Same user ${user.email} - syncing localStorage to backend`);
    // Don't block navigation/login UX on sync; run in background.
    syncLocalStorageToBackend(user.email).catch((err) => {
      console.error('Background sync after login failed:', err);
    });
  }

  publishAuthEvent(AUTH_EVENT_TYPES.login, {
    user,
    userEmail: user.email,
  });
};

// Async login function that makes API call
export const login = async (email, password, coach_id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, referral_coach_id: coach_id }),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        message: data.detail || 'Login failed. Please check your credentials.',
      };
    }

    if (isLearnerAppBlockedUser(data.user)) {
      return {
        success: false,
        staffOnly: true,
        message: learnerAppAccessDeniedMessage(),
      };
    }

    // Store user session data and sync with backend
    await storeUserSession(data.user, data.token || null);

    return {
      success: true,
      message: data.message || 'Login successful!',
      user: data.user,
      token: data.token || null,
    };
  } catch (error) {
    console.error('Login error:', error);
    return {
      success: false,
      message: 'Network error. Please check your connection and try again.',
    };
  }
};

// Function to check if user is premium
export const isPremiumUser = () => {
  const user = getStoredUser();
  return user?.is_premium || false;
};

// Function to redirect based on premium status
export const redirectBasedOnPremiumStatus = (navigate, coach_id = null) => {
  const user = getStoredUser();
  const isCoach = user?.role?.name === 'coach';

  if (isCoach) {
    navigate('/coaching');
  } else if (isPremiumUser()) {
    if (coach_id) {
      const encodedCoachId = encodeCoachId(coach_id);
      navigate(`/coach/${encodedCoachId}`);
    } else {
      navigate(getPremiumLandingPath(user));
    }
  } else if (
    user?.had_premium_before ||
    user?.user_type === 'premium_lapsed' ||
    user?.is_premium_lapsed
  ) {
    // Users who had premium before should go to pricing page to renew
    navigate('/pricing');
  } else {
    // New users or always-free users go to pricing
    navigate('/pricing');
  }
};

export const logout = async () => {
  const user = getStoredUser() || {};
  const userEmail = user?.email;

  try {
    // Sync current localStorage to backend before clearing
    if (userEmail) {
      console.log('Syncing localStorage to backend before logout...');
      await syncLocalStorageToBackend(userEmail, true); // Force sync
      console.log('✅ localStorage synced to backend successfully');
    }
  } catch (error) {
    console.error('Error syncing localStorage during logout:', error);
    // Continue with logout even if sync fails
  }

  await requestBackendLogout();
  clearAllCaches();

  // Clear all syncable localStorage data
  clearSyncableLocalStorage();

  // Clear userEmail to ensure fresh data load on next login
  localStorage.removeItem('userEmail');
  localStorage.removeItem('user');
  localStorage.removeItem('authToken');
  publishAuthEvent(AUTH_EVENT_TYPES.logout, { reason: 'user_logout' });

  window.location.href = '/login';
};

export const displayToast = (toast) => {
  return (
    toast.show && (
      <div
        className={`toast-notification ${toast.success ? 'success' : 'error'}`}
      >
        {toast.message}
      </div>
    )
  );
};

// Device detection utility
export const detectDevice = () => {
  const userAgent = navigator.userAgent;
  const screenWidth = window.screen.width;

  // Check for mobile user agents
  const mobileUserAgents =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  const isMobileUserAgent = mobileUserAgents.test(userAgent);

  // Check for small screen size (mobile typically < 768px)
  const isMobileScreen = screenWidth < 768;

  // Check for touch capability
  const isTouchDevice =
    'ontouchstart' in window || navigator.maxTouchPoints > 0;

  // Determine device type
  if (isMobileUserAgent || (isMobileScreen && isTouchDevice)) {
    if (
      /iPad/i.test(userAgent) ||
      (screenWidth >= 768 && screenWidth <= 1024)
    ) {
      return 'tablet';
    }
    return 'mobile';
  }

  return 'desktop';
};

// Show device recommendation modal
export const showDeviceRecommendation = (deviceType, setToast) => {
  if (deviceType === 'mobile') {
    const message =
      'For the best Liwaza experience, we recommend using a desktop, laptop, or tablet. Some features may be limited on mobile devices.';
    toastdisplay(message, true, 6000, setToast);
    return true; // Mobile detected
  }
  return false; // Not mobile
};

// Copilot conversation management functions
export const saveCopilotConversation = (
  messages,
  userEmail,
  prioritySchool,
  currentPage = null,
  step = null,
  userInfo = null
) => {
  if (prioritySchool && userEmail) {
    const conversationKey = `copilot_conversation_${userEmail}_${prioritySchool}`;
    localStorage.setItem(
      conversationKey,
      JSON.stringify({
        messages: messages,
        lastUpdated: new Date().toISOString(),
        currentPage: currentPage,
        step: step,
        userInfo: userInfo,
      })
    );
  }
};

export const loadCopilotConversation = (userEmail, prioritySchool) => {
  if (prioritySchool && userEmail) {
    const conversationKey = `copilot_conversation_${userEmail}_${prioritySchool}`;
    const saved = localStorage.getItem(conversationKey);
    if (saved) {
      try {
        const conversation = JSON.parse(saved);
        const allMessages = conversation.messages || [];
        // Return only the 10 latest messages
        return allMessages.slice(-10);
      } catch (error) {
        console.error('Error parsing saved conversation:', error);
        return [];
      }
    }
  }
  return [];
};

export const updateCopilotMessages = (
  setMessages,
  updateFunction,
  userEmail,
  prioritySchool,
  currentPage = null,
  step = null,
  userInfo = null
) => {
  if (setMessages) {
    setMessages((prev) => {
      const newMessages =
        typeof updateFunction === 'function'
          ? updateFunction(prev)
          : updateFunction;
      // Save to localStorage
      saveCopilotConversation(
        newMessages,
        userEmail,
        prioritySchool,
        currentPage,
        step,
        userInfo
      );
      return newMessages;
    });
  }
};

// Create a standardized user object for ProfilePicture component
export const createProfilePictureUser = (coach) => {
  return {
    profile_picture:
      coach.profileImage || coach.profile_image || coach.profile_picture,
    email: coach.email,
    id: coach.id,
    first_name: coach.first_name || coach.name?.split(' ')[0],
    last_name: coach.last_name || coach.name?.split(' ').slice(1).join(' '),
  };
};

// Coach data localStorage utilities
export const COACH_CACHE_PREFIX = 'liwaza_coach_';
export const COACH_CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

/**
 * Save coach data to localStorage with timestamp
 * @param {string} coachId - Coach identifier (email or id)
 * @param {object} coachData - Coach profile data to cache
 */
export const saveCoachToLocalStorage = (coachId, coachData) => {
  try {
    const cacheKey = `${COACH_CACHE_PREFIX}${coachId}`;
    const cacheData = {
      data: coachData,
      timestamp: Date.now(),
      version: '1.0', // For future cache invalidation if data structure changes
    };

    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    console.log(`Coach data cached for: ${coachId}`);
  } catch (error) {
    console.error('Error saving coach data to localStorage:', error);
    // Don't throw - localStorage failure shouldn't break the app
  }
};

/**
 * Get coach data from localStorage if valid and not expired
 * @param {string} coachId - Coach identifier (email or id)
 * @returns {object|null} - Coach data if valid, null if not found/expired
 */
export const getCoachFromLocalStorage = (coachId) => {
  try {
    const cacheKey = `${COACH_CACHE_PREFIX}${coachId}`;
    const cachedItem = localStorage.getItem(cacheKey);

    if (!cachedItem) {
      return null;
    }

    const { data, timestamp, version } = JSON.parse(cachedItem);

    // Check if cache is expired
    const isExpired = Date.now() - timestamp > COACH_CACHE_EXPIRY;
    if (isExpired) {
      console.log(`Coach cache expired for: ${coachId}`);
      clearCoachFromLocalStorage(coachId);
      return null;
    }

    // Check version compatibility (for future use)
    if (version !== '1.0') {
      console.log(`Coach cache version mismatch for: ${coachId}`);
      clearCoachFromLocalStorage(coachId);
      return null;
    }

    console.log(`Coach data loaded from cache: ${coachId}`);
    return data;
  } catch (error) {
    console.error('Error reading coach data from localStorage:', error);
    // Clear corrupted cache and return null
    clearCoachFromLocalStorage(coachId);
    return null;
  }
};

/**
 * Remove specific coach data from localStorage
 * @param {string} coachId - Coach identifier to remove
 */
export const clearCoachFromLocalStorage = (coachId) => {
  try {
    const cacheKey = `${COACH_CACHE_PREFIX}${coachId}`;
    localStorage.removeItem(cacheKey);
    console.log(`Coach cache cleared for: ${coachId}`);
  } catch (error) {
    console.error('Error clearing coach data from localStorage:', error);
  }
};

/**
 * Clear all cached coach data (for cleanup or logout)
 */
export const clearAllCoachCache = () => {
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(COACH_CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
    console.log(`Cleared ${keysToRemove.length} coach cache entries`);
  } catch (error) {
    console.error('Error clearing all coach cache:', error);
  }
};

/**
 * Get cache statistics for debugging
 */
export const getCoachCacheStats = () => {
  try {
    const stats = {
      totalEntries: 0,
      totalSize: 0,
      entries: [],
    };

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(COACH_CACHE_PREFIX)) {
        const value = localStorage.getItem(key);
        const coachId = key.replace(COACH_CACHE_PREFIX, '');

        try {
          // eslint-disable-next-line no-unused-vars
          const { timestamp, data } = JSON.parse(value);
          const age = Date.now() - timestamp;
          const isExpired = age > COACH_CACHE_EXPIRY;

          stats.entries.push({
            coachId,
            age: Math.round(age / (1000 * 60)), // age in minutes
            isExpired,
            size: value.length,
          });

          stats.totalEntries++;
          stats.totalSize += value.length;
        } catch (e) {
          // Corrupted entry
          stats.entries.push({
            coachId,
            corrupted: true,
            size: value.length,
          });
        }
      }
    }

    return stats;
  } catch (error) {
    console.error('Error getting cache stats:', error);
    return { error: error.message };
  }
};

/**
 * Pronunciation function using Web Speech API
 * @param {string} word - The word to pronounce
 */
const _LIWAZA_BAD_TTS_VOICE_NAME =
  /(zarvox|whisper|trinoids|bells|boing|bubbles|cellos|good news|bad news|pipe organ|hysterical)/i;
const _LIWAZA_PREFERRED_TTS_VOICE_NAME =
  /(google.*english|samantha|daniel|karen|serena|moira|tessa|alex|microsoft.*(aria|jenny|guy|david|zira))/i;

const pickBestSpeechSynthesisVoice = (voices, lang = 'en-US') => {
  if (!Array.isArray(voices) || voices.length === 0) return null;
  const targetLang = (lang || 'en-US').toLowerCase();
  const baseLang = targetLang.split('-')[0];

  const candidates = voices
    .filter((v) => v?.lang?.toLowerCase().startsWith(baseLang))
    .filter((v) => !_LIWAZA_BAD_TTS_VOICE_NAME.test(v?.name || ''));

  if (candidates.length === 0) return null;

  const exactLang = candidates.filter(
    (v) => (v?.lang || '').toLowerCase() === targetLang
  );
  const pool = exactLang.length ? exactLang : candidates;

  return (
    pool.find((v) => _LIWAZA_PREFERRED_TTS_VOICE_NAME.test(v?.name || '')) ||
    pool.find((v) => v?.default) ||
    pool[0] ||
    null
  );
};

export const speakWord = (word, options = {}) => {
  if (!word || typeof window === 'undefined') return;
  if (!('speechSynthesis' in window)) {
    console.warn('Speech synthesis not supported in this browser');
    return;
  }

  const lang = options.lang || 'en-US';
  const rate = typeof options.rate === 'number' ? options.rate : 0.9;
  const pitch = typeof options.pitch === 'number' ? options.pitch : 1;
  const volume = typeof options.volume === 'number' ? options.volume : 1;
  const attempt = typeof options._attempt === 'number' ? options._attempt : 0;

  const voices = window.speechSynthesis.getVoices();
  if ((!voices || voices.length === 0) && attempt < 2) {
    // Some browsers populate voices async; retry briefly to avoid "weird" default voice.
    window.setTimeout(() => {
      speakWord(word, {
        ...options,
        lang,
        rate,
        pitch,
        volume,
        _attempt: attempt + 1,
      });
    }, 180);
    return;
  }

  window.speechSynthesis.cancel();

  const utterance = new SpeechSynthesisUtterance(word);
  utterance.lang = lang;
  utterance.rate = rate;
  utterance.pitch = pitch;
  utterance.volume = volume;

  const chosenVoice = pickBestSpeechSynthesisVoice(voices, lang);
  if (chosenVoice) utterance.voice = chosenVoice;

  window.speechSynthesis.speak(utterance);
};

// Cache for dictionary API audio URLs to avoid repeated fetches
const pronunciationCache = {};

/**
 * Enhanced pronunciation function that tries to fetch real audio from
 * the Free Dictionary API first, falling back to the Web Speech API.
 * Returns a promise that resolves when playback starts (or falls back).
 *
 * @param {string} word - The word to pronounce
 * @returns {Promise<void>}
 */
/**
 * Fetch IPA / audio pronunciation for a word via Liwaza backend.
 * @param {string} word
 * @returns {Promise<{word: string, phonetic?: string, audio_url?: string, definition_en?: string, part_of_speech?: string, source?: string}|null>}
 */
const toAbsoluteAudioUrl = (url) => {
  if (!url) return null;
  return url.startsWith('//') ? `https:${url}` : url;
};

export const cachePronunciationAudioUrl = (word, audioUrl) => {
  const lemma = extractPronounceLemma(word);
  if (!lemma) return;
  const key = lemma.toLowerCase();
  if (!audioUrl) {
    pronunciationCache[key] = 'NO_AUDIO';
    return;
  }
  pronunciationCache[key] = toAbsoluteAudioUrl(audioUrl);
};

export const fetchWordPronunciation = async (word) => {
  if (!word) return null;
  const cleanWord = extractPronounceLemma(word) || String(word).trim();
  if (!cleanWord) return null;

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/gre/words/pronounce?word=${encodeURIComponent(cleanWord)}`,
      {
        method: 'GET',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }
    );

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    if (data?.audio_url) {
      cachePronunciationAudioUrl(cleanWord, data.audio_url);
    }
    return data;
  } catch {
    return null;
  }
};

export const prefetchWordPronunciations = async (words = []) => {
  const unique = [
    ...new Set(
      (Array.isArray(words) ? words : [])
        .map((word) => extractPronounceLemma(word))
        .filter(Boolean)
    ),
  ];
  const results = await Promise.all(
    unique.map(async (lemma) => {
      const data = await fetchWordPronunciation(lemma);
      return [lemma.toLowerCase(), data];
    })
  );
  return Object.fromEntries(results);
};

export const pronounceWord = async (word, preferredAudioUrl) => {
  if (!word) return;

  const lemma = extractPronounceLemma(word) || String(word).trim();
  const cleanWord = lemma.toLowerCase();

  const playUrl = async (url) => {
    const audio = new Audio(url);
    audio.volume = 0.85;
    await audio.play();
  };

  if (preferredAudioUrl) {
    try {
      const absoluteUrl = toAbsoluteAudioUrl(preferredAudioUrl);
      pronunciationCache[cleanWord] = absoluteUrl;
      await playUrl(absoluteUrl);
      return;
    } catch {
      // fall through
    }
  }

  if (pronunciationCache[cleanWord] === 'NO_AUDIO') {
    speakWord(lemma);
    return;
  }

  if (pronunciationCache[cleanWord]) {
    try {
      await playUrl(pronunciationCache[cleanWord]);
      return;
    } catch {
      speakWord(lemma);
      return;
    }
  }

  // Play immediately via speech synthesis; warm the dictionary cache in the background.
  speakWord(lemma);
  fetchWordPronunciation(lemma).catch(() => {});
};

// ============================================================================
// LOCALSTORAGE SYNC WITH BACKEND UTILITIES
// ============================================================================

/**
 * Keys that should be synced with backend
 * Add new keys here as needed
 */
const BACKEND_CONFIG_SCOPE = {
  bootstrap: 'bootstrap',
  full: 'full',
  journey: 'journey',
  legacyFlashcards: 'legacy_flashcards',
};

const SYNC_KEYS = [
  // GRE and Vocabulary
  'gre_vocabulary_enrollment',
  'liwaza_words_date',
  'liwaza_words_count',
  'gre_words_challenge_data',
  'gre_vocab_mastery_progress',
  'gre_concepts_mastery_progress',

  // Test and Lesson Progress
  'gre_full_test_progress',
  'gre_sentence_equivalence_progress',
  'gre_text_completion_progress',
  'gre_verbal_tips_read_status',
  'lesson_completion_data',
  'vocabulary_article_progress',
  'concepts_mastery_progress',
  'savedConcepts',

  // User Preferences and Settings
  'onboardingTasks',
  'selectedPrioritySchool',
  'voice_minutes_data',

  // Interview and Communication
  'interview_progress',
  'listening_progress',
  'reading_progress',
  'writing_progress',

  // Profile and CV/Essays
  'cv_progress',
  'essays_progress',
  '_liwaza_profile_assessment',
  '_liwaza_student_profile',

  // Community
  'liwaza_home_summary',
  'liwaza_community_announcement_seen',

  // User Progress and Exam Data
  'userProgress',

  // Applications and Training
  'liwaza-applications',
  'liwaza-training-plan',

  // Dynamic keys with patterns are handled separately in shouldSyncKey()
  // - liwaza_verbal_progress_*
  // - liwaza_quant_progress_*
  // - liwaza_reading_progress_*
  // - liwaza_reading_passages_*
  // - gre_vocab_article_progress_*
  // - article_exercises_*
  // - copilot_conversation_*
];

const UNSAFE_ACCOUNT_STORAGE_KEYS = new Set([
  'completedTests',
  'englishExam',
  'mockExam',
  'gre_quant_track',
  'gre_quant_diagnostic_score',
  'gre_words_played',
  'lesson_completion_data',
  'userProgress',
  'interviewHistory',
  'voiceActiveSession',
  'liwaza_pending_speaking_submission',
  'cvPdfUrl',
  'current-application-school',
  'booked_coaching_session',
  'liwaza_validated_bookings',
  'tage_assigned_coach',
]);

const isUnsafeAccountStorageKey = (key) => {
  if (!key) return false;
  if (UNSAFE_ACCOUNT_STORAGE_KEYS.has(key)) return true;
  if (/^full-test-\d+-(completion-date|detailed-results)$/.test(key)) {
    return true;
  }
  if (/^(reading|listening|writing|speaking)TestResults_[^_]+$/.test(key)) {
    return true;
  }
  if (
    /_(savedEssays|savedEvaluations|cvAnalysisResult|liwaza_student_profile|liwaza_profile_assessment)$/.test(
      key
    )
  ) {
    return true;
  }
  if (/^tage_lesson_progress_[^_]+$/.test(key)) {
    return true;
  }
  if (/^tage_daily_progress_\d{4}-\d{2}-\d{2}$/.test(key)) {
    return true;
  }
  if (/^tage_full_test_.+_results$/.test(key)) {
    return true;
  }
  return false;
};

const getGreScopedCompletionDateKey = (testId, userEmail) =>
  `gre_completion_date_${normalizeStorageUserEmail(userEmail)}_${testId}`;

const getGreScopedDetailedResultsKey = (testId, userEmail) =>
  `gre_detailed_results_${normalizeStorageUserEmail(userEmail)}_${testId}`;

const LEGACY_BACKEND_CONFIG_KEYS_TO_SCOPE = new Set([
  'completedTests',
  'completedListeningTest',
  'interviewHistory',
  'tage_notebook',
  'tage_completed_lessons',
  'tage_diagnostic_results',
  'tage_full_test_completed',
  'tage_school_selections',
  'tage_full_test_results',
  'gre_quant_track',
  'gre_quant_diagnostic_score',
  'gre_words_played',
  'lesson_completion_data',
  'userProgress',
]);

const migrateBackendConfigurationEntryForUser = (key, value, userEmail) => {
  if (!key || !userEmail) return { key, value };

  const fullTestMatch = key.match(
    /^(full-test-\d+)-(completion-date|detailed-results)$/
  );
  if (fullTestMatch) {
    const [, testId, type] = fullTestMatch;
    return {
      key:
        type === 'completion-date'
          ? getGreScopedCompletionDateKey(testId, userEmail)
          : getGreScopedDetailedResultsKey(testId, userEmail),
      value,
    };
  }

  if (
    LEGACY_BACKEND_CONFIG_KEYS_TO_SCOPE.has(key) ||
    /^tage_(lesson_progress|daily_progress|full_test_.*_results)/.test(key) ||
    /^(reading|listening|writing|speaking)TestResults_/.test(key) ||
    /_(savedEssays|savedEvaluations|cvAnalysisResult|liwaza_student_profile|liwaza_profile_assessment)$/.test(
      key
    )
  ) {
    return {
      key: getAccountScopedLocalStorageKey(key, userEmail),
      value,
    };
  }

  return { key, value };
};

const isLessonCompletionStorageKey = (key) =>
  key === 'lesson_completion_data' || key.startsWith('lesson_completion_data_');

const isUserProgressStorageKey = (key) =>
  key === 'userProgress' || key.startsWith('userProgress_');

/**
 * Check if a key should be synced with backend
 * @param {string} key - localStorage key
 * @returns {boolean} - whether the key should be synced
 */
const shouldSyncKey = (key) => {
  if (!key) return false;

  // Explicitly exclude large/derived caches that should not be persisted remotely
  if (key === 'liwaza_spaces' || key === 'liwaza-spaces') return false;

  // Direct match
  if (SYNC_KEYS.includes(key)) return true;

  // Pattern matches for dynamic keys
  if (key.startsWith('copilot_conversation_')) return true;
  if (key.startsWith('liwaza_verbal_progress_')) return true; // Verbal progress tracking
  if (key.startsWith('liwaza_quant_progress_')) return true; // Quant progress tracking
  if (key.startsWith('liwaza_reading_progress_')) return true; // Reading progress tracking
  if (key.startsWith('liwaza_reading_passages_')) return false; // Ephemeral API cache (10min TTL), not user data
  if (key.startsWith('gre_vocab_article_progress_')) return true; // Vocab article progress
  if (key.startsWith('gre_vocab_read_articles_')) return true; // Vocab read state
  if (key.startsWith('gre_vocab_opened_articles_')) return true; // Vocab opened state
  if (key.startsWith('gre_completion_date_')) return true; // Account-scoped GRE completion date
  if (key.startsWith('gre_detailed_results_')) return true; // Account-scoped GRE detailed result
  if (key.startsWith('gre_result_meta_')) return true; // Account-scoped GRE result metadata
  if (key.startsWith('gre_pending_sync_')) return true; // Account-scoped GRE pending sync
  if (key.startsWith('liwaza_quant_practice_history_')) return true;
  if (key.startsWith('liwaza_quant_seen_questions_')) return true;
  if (key.startsWith('completedTests_')) return true;
  if (key.startsWith('lesson_completion_data_')) return true;
  if (key.startsWith('userProgress_')) return true;
  if (key.startsWith('completedListeningTest_')) return true;
  if (key.startsWith('gre_words_played_')) return true;
  if (key.startsWith('gre_quant_track_')) return true;
  if (key.startsWith('gre_quant_diagnostic_score_')) return true;
  if (/^(reading|listening|writing|speaking)TestResults_.*_/.test(key)) return true;
  if (key.startsWith('interviewHistory_')) return true;
  if (key.startsWith('tage_notebook_')) return true;
  if (key.startsWith('tage_completed_lessons_')) return true;
  if (key.startsWith('tage_diagnostic_results_')) return true;
  if (key.startsWith('tage_full_test_completed_')) return true;
  if (key.startsWith('tage_school_selections_')) return true;
  if (key.startsWith('tage_full_test_results_')) return true;
  if (key.startsWith('tage_daily_progress_')) return true;
  if (key.startsWith('tage_lesson_progress_')) return true;
  if (
    /_(savedEssays|savedEvaluations|cvAnalysisResult|liwaza_student_profile|liwaza_profile_assessment)_[^_]+$/.test(
      key
    )
  ) {
    return true;
  }
  if (key.startsWith('article_exercises_')) return false; // Exercises are re-fetchable from API, too large to sync
  if (key.startsWith('article_')) return false; // Article metadata is re-fetchable from API
  if (key.startsWith('languageJourney_')) return true; // English learning journey progress
  if (key.startsWith('journey_day_override_')) return true; // Admin-controlled current IELTS day
  if (key.startsWith('voice_limits_override_')) return true; // Admin-controlled voice limits

  // Explicitly exclude coach cache
  if (key.startsWith('liwaza_coach_')) return false; // Coach cache is handled separately

  return false;
};

/**
 * Get all localStorage data that should be synced
 * @returns {object} - object containing all sync-worthy localStorage data
 */
const getAllSyncableLocalStorageData = () => {
  const syncData = {};

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && shouldSyncKey(key)) {
      try {
        const value = localStorage.getItem(key);
        // Try to parse as JSON, if it fails store as string
        try {
          syncData[key] = JSON.parse(value);
        } catch {
          syncData[key] = value;
        }
      } catch (error) {
        console.warn(`Error reading localStorage key ${key}:`, error);
      }
    }
  }

  return syncData;
};

const compactUserProgressEntryForBackend = (entry) => {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
    return entry;
  }

  const compactEntry = { ...entry };

  if (compactEntry.results && typeof compactEntry.results === 'object') {
    compactEntry.results = { ...compactEntry.results };

    // Keep the structured assessment payload and key coaching feedback,
    // but drop duplicate score fields already present under `assessment`.
    if (compactEntry.results.assessment) {
      delete compactEntry.results.overallBandScore;
      delete compactEntry.results.fluencyAndCoherence;
      delete compactEntry.results.lexicalResource;
      delete compactEntry.results.grammaticalRangeAndAccuracy;
      delete compactEntry.results.pronunciation;
    }
  }

  delete compactEntry.backendResult;
  delete compactEntry.transcript;

  return compactEntry;
};

const compactUserProgressForBackend = (userProgress) => {
  if (!Array.isArray(userProgress)) return userProgress;
  return userProgress.map(compactUserProgressEntryForBackend);
};

const compactJourneyForBackend = (journeyData) => {
  if (
    !journeyData ||
    typeof journeyData !== 'object' ||
    Array.isArray(journeyData)
  ) {
    return journeyData;
  }

  if (!Array.isArray(journeyData.journey)) {
    return journeyData;
  }

  return {
    ...journeyData,
    journey: journeyData.journey.map((day) => ({
      ...day,
      progress: undefined,
      validationCriteria: undefined,
      speakingTopic: undefined,
      speakingCategory: undefined,
      activities: Array.isArray(day.activities)
        ? day.activities.map((activity) => ({
            ...activity,
            vocabulary: undefined,
            opinionPrompt: undefined,
            infoContent: undefined,
            cueCard: undefined,
            topicsToReview: undefined,
            component: undefined,
          }))
        : day.activities,
    })),
  };
};

/**
 * Save user configuration to backend
 * @param {string} userEmail - User's email
 * @param {object} configuration - Configuration data to save
 * @returns {Promise<boolean>} - success status
 */
const sanitizeConfigurationForBackend = (configuration) => {
  if (!configuration || typeof configuration !== 'object') return configuration;

  const sanitized = { ...configuration };

  Object.entries(configuration).forEach(([key]) => {
    if (isUnsafeAccountStorageKey(key)) {
      delete sanitized[key];
      return;
    }

    if (isUserProgressStorageKey(key)) {
      sanitized[key] = compactUserProgressForBackend(configuration[key]);
      return;
    }

    if (key === 'liwaza_home_summary') {
      delete sanitized[key];
      return;
    }

    if (key.startsWith('languageJourney_')) {
      sanitized[key] = compactJourneyForBackend(configuration[key]);
      return;
    }

    // Strip article_exercises entirely (re-fetchable from API)
    if (key.startsWith('article_exercises_')) {
      delete sanitized[key];
      return;
    }

    // Strip article caches entirely - articles are fetched directly from the GRE API.
    if (key.startsWith('article_')) {
      delete sanitized[key];
      return;
    }

    // Public/community reference data should not live in user configuration.
    if (
      key === 'liwaza_tags' ||
      key === 'liwaza_spaces' ||
      key === 'liwaza-spaces'
    ) {
      delete sanitized[key];
      return;
    }

    // Legacy flashcard blobs are migrated separately and should not keep bloating config.
    if (
      key.startsWith('gre_flashcards_') ||
      key.startsWith('gre_flashcard_mastered_') ||
      key.startsWith('gre_flashcard_review_history_')
    ) {
      delete sanitized[key];
      return;
    }

    // Strip reading passage caches (ephemeral API caches with short TTL)
    if (key.startsWith('liwaza_reading_passages_')) {
      delete sanitized[key];
      return;
    }
  });

  return sanitized;
};

const saveUserConfigurationToBackend = async (userEmail, configuration) => {
  try {
    const sanitizedConfiguration =
      sanitizeConfigurationForBackend(configuration);
    const emailKey = String(userEmail).toLowerCase();
    const response = await fetch(`${API_BASE_URL}/api/user/app/configure/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        configuration: sanitizedConfiguration,
        merge: true,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    if (result.success) {
      clearBackendConfigurationCacheForUser(emailKey);
    }
    return result.success;
  } catch (error) {
    console.error('Error saving user configuration to backend:', error);
    return false;
  }
};

const buildBackendConfigurationQuery = (scope) => {
  const params = new URLSearchParams();
  if (scope && scope !== BACKEND_CONFIG_SCOPE.bootstrap) {
    params.set('scope', scope);
  }
  const query = params.toString();
  return query ? `?${query}` : '';
};

const getBackendConfigRequestKey = (userEmail, scope) =>
  `${String(userEmail).toLowerCase()}::${scope || BACKEND_CONFIG_SCOPE.bootstrap}`;

const clearBackendConfigurationCacheForUser = (userEmail) => {
  const emailKey = String(userEmail).toLowerCase();
  Array.from(_backendConfigFetchCoordinator.cacheByUser.keys()).forEach(
    (key) => {
      if (key.startsWith(`${emailKey}::`)) {
        _backendConfigFetchCoordinator.cacheByUser.delete(key);
      }
    }
  );
};

/**
 * Load user configuration from backend
 * @param {string} userEmail - User's email
 * @returns {Promise<object|null>} - configuration data or null
 */
const loadUserConfigurationFromBackend = async (userEmail) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/user/app/get-configuration${buildBackendConfigurationQuery(
        BACKEND_CONFIG_SCOPE.bootstrap
      )}`,
      {
        method: 'GET',
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result.success ? result.configuration : null;
  } catch (error) {
    console.error('Error loading user configuration from backend:', error);
    return null;
  }
};

// -----------------------------------------------------------------------------
// Backend configuration fetch (dedupe + short TTL cache)
// -----------------------------------------------------------------------------
// Login bootstrapping can call `get-configuration` multiple times (load + sync).
// Deduping here avoids duplicate network calls and speeds up login.
const _backendConfigFetchCoordinator = {
  inFlightByUser: new Map(), // userEmail(lowercased) -> Promise<object|null>
  cacheByUser: new Map(), // userEmail(lowercased) -> { configuration, fetchedAt }
};

const _BACKEND_CONFIG_CACHE_TTL_MS = 15_000;

const _fetchBackendConfigOnce = async (
  userEmail,
  scope = BACKEND_CONFIG_SCOPE.bootstrap,
  maxRetries = 0
) => {
  let lastError = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/user/app/get-configuration${buildBackendConfigurationQuery(
          scope
        )}`,
        {
          method: 'GET',
          headers: getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.success ? result.configuration : null;
    } catch (error) {
      lastError = error;
      if (attempt < maxRetries) {
        const delay = 500 * Math.pow(2, attempt); // 500, 1000, 2000...
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  console.error('Error loading user configuration from backend:', lastError);
  return null;
};

/**
 * Fetch backend configuration with in-flight dedupe + short TTL cache
 * @param {string} userEmail
 * @param {{forceRefresh?: boolean, maxRetries?: number, scope?: string}} options
 * @returns {Promise<object|null>}
 */
export const fetchBackendConfiguration = async (
  userEmail,
  {
    forceRefresh = false,
    maxRetries = 0,
    scope = BACKEND_CONFIG_SCOPE.bootstrap,
  } = {}
) => {
  if (!userEmail) return null;
  const requestKey = getBackendConfigRequestKey(userEmail, scope);
  const now = Date.now();

  const cached = _backendConfigFetchCoordinator.cacheByUser.get(requestKey);
  if (
    !forceRefresh &&
    cached &&
    now - (cached.fetchedAt || 0) < _BACKEND_CONFIG_CACHE_TTL_MS
  ) {
    return cached.configuration;
  }

  const existingInFlight =
    _backendConfigFetchCoordinator.inFlightByUser.get(requestKey);
  if (existingInFlight) return existingInFlight;

  const p = (async () => {
    const configuration = await _fetchBackendConfigOnce(
      userEmail,
      scope,
      maxRetries
    );
    _backendConfigFetchCoordinator.cacheByUser.set(requestKey, {
      configuration,
      fetchedAt: Date.now(),
    });
    return configuration;
  })();

  _backendConfigFetchCoordinator.inFlightByUser.set(requestKey, p);
  try {
    return await p;
  } finally {
    _backendConfigFetchCoordinator.inFlightByUser.delete(requestKey);
  }
};

/**
 * Count completed activities in journey data (for deep comparison)
 * @param {object} journeyData - Journey data object
 * @returns {number} - Number of completed activities
 */
const countJourneyCompletedActivities = (journeyData) => {
  if (!journeyData?.journey || !Array.isArray(journeyData.journey)) return 0;
  return journeyData.journey.reduce((total, day) => {
    return (
      total + (day.activities?.filter((a) => a.completed === true).length || 0)
    );
  }, 0);
};

// -----------------------------------------------------------------------------
// Backend sync coordinator (dedupe/throttle)
// -----------------------------------------------------------------------------
// Multiple parts of the app can trigger backend syncs in quick succession (and
// "critical key" updates can trigger immediate syncs). Without coordination,
// this can spam `get-configuration` + `configure` requests in parallel.
const _backendSyncCoordinator = {
  inFlightByUser: new Map(), // userEmail(lowercased) -> Promise<boolean>
  needsResyncByUser: new Map(), // userEmail(lowercased) -> boolean
  lastStartByUser: new Map(), // userEmail(lowercased) -> number (ms)
  scheduledByUser: new Map(), // userEmail(lowercased) -> timeoutId
};

const _SYNC_THROTTLE_MS = 1500;

/**
 * Merge two journeys that are the same (combine completed activities)
 * Used during sync to preserve progress from both local and backend
 * @param {object} journey1 - First journey
 * @param {object} journey2 - Second journey
 * @returns {object} - Merged journey with all completed activities from both
 */
const mergeJourneyProgressForSync = (journey1, journey2) => {
  // Prefer the richer structure as base so a compact backend copy does not
  // erase local runtime-only activity metadata on the current device.
  const j1Time = new Date(journey1.lastUpdated || 0).getTime();
  const j2Time = new Date(journey2.lastUpdated || 0).getTime();
  const j1Size = JSON.stringify(journey1).length;
  const j2Size = JSON.stringify(journey2).length;
  const preferJourney2AsBase =
    j2Size > j1Size || (j2Size === j1Size && j2Time > j1Time);
  const base = JSON.parse(
    JSON.stringify(preferJourney2AsBase ? journey2 : journey1)
  );
  const other = preferJourney2AsBase ? journey1 : journey2;

  // Merge completed activities from both journeys
  if (other.journey && base.journey) {
    other.journey.forEach((otherDay, dayIndex) => {
      if (base.journey[dayIndex] && otherDay.activities) {
        otherDay.activities.forEach((otherActivity) => {
          if (otherActivity.completed) {
            // Find matching activity in base by type
            const baseActivity = base.journey[dayIndex].activities?.find(
              (a) => a.type === otherActivity.type
            );
            if (baseActivity && !baseActivity.completed) {
              // Copy completed status from other
              baseActivity.completed = true;
              baseActivity.completedAt = otherActivity.completedAt;
              if (otherActivity.score) baseActivity.score = otherActivity.score;
              if (otherActivity.testResults)
                baseActivity.testResults = otherActivity.testResults;
            }
          }
        });
      }
    });
  }

  // Update lastUpdated to now since we merged
  base.lastUpdated = new Date().toISOString();

  return base;
};

/**
 * Check if localStorage data has been reduced or emptied compared to backend
 * @param {*} localValue - Value from localStorage
 * @param {*} backendValue - Value from backend
 * @param {string} key - The localStorage key (for specialized checks)
 * @returns {boolean} - true if local is reduced/emptied compared to backend
 */
const isDataReducedOrEmptied = (localValue, backendValue, key = '') => {
  // If backend has no value, local can't be reduced
  if (!backendValue) return false;

  // If local is null/undefined/empty string, it's been emptied
  if (
    !localValue ||
    (typeof localValue === 'string' && localValue.trim() === '')
  ) {
    return true;
  }

  // Special handling for languageJourney data - compare completed activities count
  if (key.startsWith('languageJourney_')) {
    try {
      const localParsed =
        typeof localValue === 'string' ? JSON.parse(localValue) : localValue;
      const backendParsed =
        typeof backendValue === 'string'
          ? JSON.parse(backendValue)
          : backendValue;

      const localCompletedCount = countJourneyCompletedActivities(localParsed);
      const backendCompletedCount =
        countJourneyCompletedActivities(backendParsed);
      const localStartTime = new Date(localParsed?.startDate || 0).getTime();
      const backendStartTime = new Date(
        backendParsed?.startDate || 0
      ).getTime();
      const isIntentionalJourneyReset =
        localParsed?.levelUpdatedFromTest === true &&
        localStartTime >= backendStartTime &&
        (localParsed?.currentLevel !== backendParsed?.currentLevel ||
          localParsed?.targetLevel !== backendParsed?.targetLevel ||
          localStartTime !== backendStartTime);

      console.log(
        `Journey comparison - Local completed: ${localCompletedCount}, Backend completed: ${backendCompletedCount}`
      );

      if (isIntentionalJourneyReset) {
        console.log(
          'Journey comparison - allowing intentional level-update journey reset to sync'
        );
        return false;
      }

      const localAdminUpdateTime = new Date(
        localParsed?.manualAdminUpdateAt || 0
      ).getTime();
      const backendAdminUpdateTime = new Date(
        backendParsed?.manualAdminUpdateAt || 0
      ).getTime();

      if (backendAdminUpdateTime > localAdminUpdateTime) {
        console.warn(
          'Journey admin update detected on backend. Preserving backend version.'
        );
        return true;
      }

      // If backend has more completed activities, local is reduced
      if (backendCompletedCount > localCompletedCount) {
        console.warn(
          `Journey data regression detected: backend has ${backendCompletedCount} completed activities, local only has ${localCompletedCount}`
        );
        return true;
      }
      return false;
    } catch (e) {
      console.error('Error comparing journey data:', e);
      // If parsing fails, be conservative and don't overwrite
      return true;
    }
  }

  // Special handling for lesson_completion_data - compare number of completed lessons
  if (isLessonCompletionStorageKey(key)) {
    try {
      const localParsed =
        typeof localValue === 'string' ? JSON.parse(localValue) : localValue;
      const backendParsed =
        typeof backendValue === 'string'
          ? JSON.parse(backendValue)
          : backendValue;

      const localCount = Object.keys(localParsed || {}).length;
      const backendCount = Object.keys(backendParsed || {}).length;

      if (backendCount > localCount) {
        console.warn(
          `Lesson completion data regression detected: backend has ${backendCount} lessons, local only has ${localCount}`
        );
        return true;
      }
      return false;
    } catch (e) {
      return true;
    }
  }

  // Special handling for GRE Quant/Verbal progress - compare by timestamp and total data
  if (
    key.startsWith('liwaza_quant_progress_') ||
    key.startsWith('liwaza_verbal_progress_')
  ) {
    try {
      const localParsed =
        typeof localValue === 'string' ? JSON.parse(localValue) : localValue;
      const backendParsed =
        typeof backendValue === 'string'
          ? JSON.parse(backendValue)
          : backendValue;

      // Compare timestamps - if backend is newer, local might be stale
      const localTimestamp = localParsed?.timestamp || 0;
      const backendTimestamp = backendParsed?.timestamp || 0;

      // Compare data depth - count exercises, sessions, etc.
      const countProgressItems = (data) => {
        if (!data?.data) return 0;
        let count = 0;
        const progressData = data.data;

        // Count completed exercises/sessions in various sections
        if (progressData.overall_summary) {
          count += progressData.overall_summary.total_study_time_minutes || 0;
          count += (progressData.overall_summary.current_streak || 0) * 100; // Weight streak heavily
        }
        if (progressData.vocabulary_mastery) {
          count += progressData.vocabulary_mastery.total_words_encountered || 0;
          count += progressData.vocabulary_mastery.total_words_mastered || 0;
        }
        if (progressData.text_completion) {
          count += progressData.text_completion.total_questions_attempted || 0;
        }
        if (progressData.sentence_equivalence) {
          count +=
            progressData.sentence_equivalence.total_questions_attempted || 0;
        }
        if (progressData.reading_comprehension) {
          count +=
            progressData.reading_comprehension.total_passages_completed || 0;
        }
        if (progressData.concept_lessons) {
          count += progressData.concept_lessons.completed_lessons || 0;
        }
        return count;
      };

      const localProgressCount = countProgressItems(localParsed);
      const backendProgressCount = countProgressItems(backendParsed);

      console.log(
        `GRE Progress (${key}) - Local: ${localProgressCount} items (ts: ${localTimestamp}), Backend: ${backendProgressCount} items (ts: ${backendTimestamp})`
      );

      // If backend has significantly more progress, local is reduced
      if (backendProgressCount > localProgressCount) {
        console.warn(
          `GRE Progress regression detected: backend has ${backendProgressCount} progress items, local only has ${localProgressCount}`
        );
        return true;
      }

      // If same progress but backend is newer (by more than 1 hour), prefer backend
      if (
        backendProgressCount === localProgressCount &&
        backendTimestamp > localTimestamp + 3600000
      ) {
        console.warn(
          `GRE Progress: same items but backend is significantly newer`
        );
        return true;
      }

      return false;
    } catch (e) {
      console.error('Error comparing GRE progress data:', e);
      return true; // Conservative: don't overwrite if can't compare
    }
  }

  // Special handling for concepts_mastery_progress - array of completed lessons
  if (
    key === 'concepts_mastery_progress' ||
    key === 'gre_concepts_mastery_progress'
  ) {
    try {
      const localParsed =
        typeof localValue === 'string' ? JSON.parse(localValue) : localValue;
      const backendParsed =
        typeof backendValue === 'string'
          ? JSON.parse(backendValue)
          : backendValue;

      const localCount = Array.isArray(localParsed) ? localParsed.length : 0;
      const backendCount = Array.isArray(backendParsed)
        ? backendParsed.length
        : 0;

      if (backendCount > localCount) {
        console.warn(
          `Concepts mastery regression detected: backend has ${backendCount} lessons, local only has ${localCount}`
        );
        return true;
      }
      return false;
    } catch (e) {
      return true;
    }
  }

  // Special handling for GRE vocab article progress - compare by article count
  if (key.startsWith('gre_vocab_article_progress_')) {
    try {
      const localParsed =
        typeof localValue === 'string' ? JSON.parse(localValue) : localValue;
      const backendParsed =
        typeof backendValue === 'string'
          ? JSON.parse(backendValue)
          : backendValue;

      const localCount = Object.keys(localParsed || {}).length;
      const backendCount = Object.keys(backendParsed || {}).length;

      if (backendCount > localCount) {
        console.warn(
          `GRE vocab article progress regression detected: backend has ${backendCount} articles, local only has ${localCount}`
        );
        return true;
      }
      return false;
    } catch (e) {
      return true;
    }
  }

  // Special handling for voice_minutes_data - compare total usage
  if (key === 'voice_minutes_data') {
    try {
      const localParsed =
        typeof localValue === 'string' ? JSON.parse(localValue) : localValue;
      const backendParsed =
        typeof backendValue === 'string'
          ? JSON.parse(backendValue)
          : backendValue;

      const localMinutes = localParsed?.totalMinutesUsed || 0;
      const backendMinutes = backendParsed?.totalMinutesUsed || 0;
      const localSessions = localParsed?.sessions?.length || 0;
      const backendSessions = backendParsed?.sessions?.length || 0;

      // Compare by total usage (more is more progress)
      if (backendMinutes > localMinutes || backendSessions > localSessions) {
        console.warn(
          `Voice minutes regression detected: backend has ${backendMinutes}min/${backendSessions} sessions, local has ${localMinutes}min/${localSessions} sessions`
        );
        return true;
      }
      return false;
    } catch (e) {
      return true;
    }
  }

  // Special handling for training plan - compare by existence and completeness
  if (key === 'liwaza-training-plan') {
    try {
      const localParsed =
        typeof localValue === 'string' ? JSON.parse(localValue) : localValue;
      const backendParsed =
        typeof backendValue === 'string'
          ? JSON.parse(backendValue)
          : backendValue;

      // Count study activities completed
      const countCompletedActivities = (plan) => {
        if (!plan) return 0;
        let count = 0;
        if (plan.studyActivities) {
          count += plan.studyActivities.filter((a) => a.completed).length;
        }
        if (plan.weeklyGoals) {
          count += plan.weeklyGoals.filter((g) => g.completed).length;
        }
        return count;
      };

      const localCompleted = countCompletedActivities(localParsed);
      const backendCompleted = countCompletedActivities(backendParsed);

      if (backendCompleted > localCompleted) {
        console.warn(
          `Training plan regression detected: backend has ${backendCompleted} completed, local has ${localCompleted}`
        );
        return true;
      }
      return false;
    } catch (e) {
      return true;
    }
  }

  // Special handling for MBA applications - compare by count and status
  if (key === 'liwaza-applications') {
    try {
      const localParsed =
        typeof localValue === 'string' ? JSON.parse(localValue) : localValue;
      const backendParsed =
        typeof backendValue === 'string'
          ? JSON.parse(backendValue)
          : backendValue;

      const localCount = Array.isArray(localParsed) ? localParsed.length : 0;
      const backendCount = Array.isArray(backendParsed)
        ? backendParsed.length
        : 0;

      if (backendCount > localCount) {
        console.warn(
          `Applications regression detected: backend has ${backendCount}, local has ${localCount}`
        );
        return true;
      }
      return false;
    } catch (e) {
      return true;
    }
  }

  // Special handling for onboarding tasks - compare completed count
  if (key === 'onboardingTasks') {
    try {
      const localParsed =
        typeof localValue === 'string' ? JSON.parse(localValue) : localValue;
      const backendParsed =
        typeof backendValue === 'string'
          ? JSON.parse(backendValue)
          : backendValue;

      const localCompleted = Array.isArray(localParsed)
        ? localParsed.filter((t) => t.completed).length
        : 0;
      const backendCompleted = Array.isArray(backendParsed)
        ? backendParsed.filter((t) => t.completed).length
        : 0;

      if (backendCompleted > localCompleted) {
        console.warn(
          `Onboarding tasks regression detected: backend has ${backendCompleted} completed, local has ${localCompleted}`
        );
        return true;
      }
      return false;
    } catch (e) {
      return true;
    }
  }

  // Special handling for copilot conversations - compare message count
  if (key.startsWith('copilot_conversation_')) {
    try {
      const localParsed =
        typeof localValue === 'string' ? JSON.parse(localValue) : localValue;
      const backendParsed =
        typeof backendValue === 'string'
          ? JSON.parse(backendValue)
          : backendValue;

      // Can be array of messages or object with messages property
      const getMessageCount = (data) => {
        if (Array.isArray(data)) return data.length;
        if (data?.messages && Array.isArray(data.messages))
          return data.messages.length;
        return 0;
      };

      const localMessages = getMessageCount(localParsed);
      const backendMessages = getMessageCount(backendParsed);

      if (backendMessages > localMessages) {
        console.warn(
          `Copilot conversation regression detected: backend has ${backendMessages} messages, local has ${localMessages}`
        );
        return true;
      }
      return false;
    } catch (e) {
      return true;
    }
  }

  // Special handling for saved concepts - compare count
  if (key === 'savedConcepts') {
    try {
      const localParsed =
        typeof localValue === 'string' ? JSON.parse(localValue) : localValue;
      const backendParsed =
        typeof backendValue === 'string'
          ? JSON.parse(backendValue)
          : backendValue;

      const localCount = Array.isArray(localParsed) ? localParsed.length : 0;
      const backendCount = Array.isArray(backendParsed)
        ? backendParsed.length
        : 0;

      if (backendCount > localCount) {
        console.warn(
          `Saved concepts regression detected: backend has ${backendCount}, local has ${localCount}`
        );
        return true;
      }
      return false;
    } catch (e) {
      return true;
    }
  }

  // Special handling for community spaces/tags - compare count
  if (key === 'liwaza_spaces' || key === 'liwaza_tags') {
    try {
      const localParsed =
        typeof localValue === 'string' ? JSON.parse(localValue) : localValue;
      const backendParsed =
        typeof backendValue === 'string'
          ? JSON.parse(backendValue)
          : backendValue;

      const localCount = Array.isArray(localParsed) ? localParsed.length : 0;
      const backendCount = Array.isArray(backendParsed)
        ? backendParsed.length
        : 0;

      if (backendCount > localCount) {
        console.warn(
          `${key} regression detected: backend has ${backendCount}, local has ${localCount}`
        );
        return true;
      }
      return false;
    } catch (e) {
      return true;
    }
  }

  // For arrays, compare lengths
  if (Array.isArray(localValue) && Array.isArray(backendValue)) {
    return localValue.length < backendValue.length;
  }

  // For objects, compare number of keys
  if (
    typeof localValue === 'object' &&
    typeof backendValue === 'object' &&
    !Array.isArray(localValue) &&
    !Array.isArray(backendValue)
  ) {
    const localKeys = Object.keys(localValue);
    const backendKeys = Object.keys(backendValue);
    return localKeys.length < backendKeys.length;
  }

  // For strings that might be JSON arrays/objects, try to parse and compare
  if (typeof localValue === 'string' && typeof backendValue === 'string') {
    try {
      const localParsed = JSON.parse(localValue);
      const backendParsed = JSON.parse(backendValue);

      if (Array.isArray(localParsed) && Array.isArray(backendParsed)) {
        return localParsed.length < backendParsed.length;
      }

      if (
        typeof localParsed === 'object' &&
        typeof backendParsed === 'object' &&
        !Array.isArray(localParsed) &&
        !Array.isArray(backendParsed)
      ) {
        return (
          Object.keys(localParsed).length < Object.keys(backendParsed).length
        );
      }
    } catch {
      // If parsing fails, compare as strings
      return localValue.length < backendValue.length;
    }
  }

  return false;
};

/**
 * Sync localStorage data to backend
 * @param {string} userEmail - User's email
 * @param {boolean} force - Force sync even if no changes detected (but ALWAYS check for data reduction)
 * @returns {Promise<boolean>} - success status
 */
export const syncLocalStorageToBackend = async (userEmail, force = false) => {
  if (!userEmail) {
    console.warn('Cannot sync localStorage: no user email provided');
    return false;
  }

  const emailKey = String(userEmail).toLowerCase();

  // If a sync is already in progress for this user, don't start another one.
  const existingInFlight = _backendSyncCoordinator.inFlightByUser.get(emailKey);
  if (existingInFlight) {
    if (force) {
      _backendSyncCoordinator.needsResyncByUser.set(emailKey, true);
    }
    return existingInFlight;
  }

  // Throttle rapid back-to-back sync triggers (common during speaking sessions).
  const now = Date.now();
  const lastStart = _backendSyncCoordinator.lastStartByUser.get(emailKey) || 0;
  if (now - lastStart < _SYNC_THROTTLE_MS) {
    _backendSyncCoordinator.needsResyncByUser.set(emailKey, true);
    // Schedule a single delayed sync so we don't drop the update.
    if (!_backendSyncCoordinator.scheduledByUser.has(emailKey)) {
      const delay = Math.max(50, _SYNC_THROTTLE_MS - (now - lastStart));
      const timeoutId = setTimeout(() => {
        _backendSyncCoordinator.scheduledByUser.delete(emailKey);
        syncLocalStorageToBackend(userEmail, true).catch(() => {});
      }, delay);
      _backendSyncCoordinator.scheduledByUser.set(emailKey, timeoutId);
    }
    return true;
  }

  _backendSyncCoordinator.lastStartByUser.set(emailKey, now);

  const performOneSync = async (forceFlag) => {
    try {
      const localData = getAllSyncableLocalStorageData();

      // Skip if no data to sync and not forced
      if (!forceFlag && Object.keys(localData).length === 0) {
        console.log('No localStorage data to sync');
        return true;
      }

      // ALWAYS check backend data to detect reductions/emptying
      // This prevents data loss even with force=true
      const backendConfig = await fetchBackendConfiguration(userEmail, {
        maxRetries: 1,
        scope: BACKEND_CONFIG_SCOPE.full,
      });

      let configurationToPersist = localData;

      if (backendConfig) {
        const keysToRemove = [];
        const keysToRestoreFromBackend = [];

        // Compare each key and mark for removal if reduced/emptied
        Object.keys(localData).forEach((key) => {
          const localValue = localData[key];
          const backendValue = backendConfig[key];

          // If key doesn't exist in backend, it's a new key - allow sync
          if (!backendConfig.hasOwnProperty(key)) {
            return; // Keep this key, it's new
          }

          // If key exists in backend, check if local is reduced/emptied
          if (isDataReducedOrEmptied(localValue, backendValue, key)) {
            console.warn(
              `🛡️ PROTECTION: Skipping sync for ${key}: localStorage data is reduced or emptied compared to backend`
            );
            keysToRemove.push(key);
            keysToRestoreFromBackend.push({ key, value: backendValue });
          }
        });

        // Remove keys that were reduced/emptied from sync payload
        keysToRemove.forEach((key) => {
          delete localData[key];
        });

        // Restore the better data from backend to localStorage
        if (keysToRestoreFromBackend.length > 0) {
          console.log(
            `🔄 Restoring ${keysToRestoreFromBackend.length} keys from backend to localStorage`
          );
          keysToRestoreFromBackend.forEach(({ key, value }) => {
            try {
              const valueToStore =
                typeof value === 'object' ? JSON.stringify(value) : value;
              localStorage.setItem(key, valueToStore);
              console.log(`✅ Restored ${key} from backend`);
            } catch (e) {
              console.error(`Failed to restore ${key}:`, e);
            }
          });
        }

        if (keysToRemove.length > 0 && Object.keys(localData).length === 0) {
          console.log(
            'Skipping sync: All localStorage data appears to be reduced or emptied compared to backend'
          );
          return true;
        }

        // Preserve remote-only keys because the configure endpoint replaces the
        // entire configuration payload rather than patching specific entries.
        configurationToPersist = {
          ...backendConfig,
          ...localData,
        };
      }

      // Skip if no data left to sync after filtering
      if (Object.keys(configurationToPersist).length === 0) {
        console.log('No localStorage data to sync after filtering reductions');
        return true;
      }

      console.log(
        `Syncing ${Object.keys(configurationToPersist).length} localStorage items to backend for user: ${userEmail}`
      );

      const success = await saveUserConfigurationToBackend(
        userEmail,
        configurationToPersist
      );

      if (success) {
        console.log('localStorage successfully synced to backend');
        // Store last sync timestamp
        localStorage.setItem('_last_backend_sync', Date.now().toString());
      } else {
        console.error('Failed to sync localStorage to backend');
      }

      return success;
    } catch (error) {
      console.error('Error during localStorage sync to backend:', error);
      return false;
    }
  };

  // Run at most 1 follow-up sync if new changes arrive mid-sync.
  const coordinatedPromise = (async () => {
    let result = true;
    let forceFlag = force;

    // If anything triggers a sync while we're running, they'll set needsResync=true.
    while (true) {
      _backendSyncCoordinator.needsResyncByUser.set(emailKey, false);
      result = await performOneSync(forceFlag);
      forceFlag = false;

      const needsResync =
        _backendSyncCoordinator.needsResyncByUser.get(emailKey) === true;
      if (!needsResync) break;

      // Small delay to allow multiple rapid writes to batch up.
      await new Promise((resolve) => setTimeout(resolve, 250));
      forceFlag = true;
    }

    return result;
  })();

  _backendSyncCoordinator.inFlightByUser.set(emailKey, coordinatedPromise);
  try {
    return await coordinatedPromise;
  } finally {
    _backendSyncCoordinator.inFlightByUser.delete(emailKey);
  }
};

/**
 * Load configuration from backend and update localStorage
 * Intelligently merges data, preferring the version with more progress
 * @param {string} userEmail - User's email
 * @param {boolean} overwriteLocal - Whether to overwrite existing localStorage data (smart merge for journey data)
 * @returns {Promise<boolean>} - success status
 */
export const loadBackendConfigurationToLocalStorage = async (
  userEmail,
  overwriteLocal = true
) => {
  if (!userEmail) {
    console.warn('Cannot load backend configuration: no user email provided');
    return false;
  }

  try {
    console.log(`Loading backend configuration for user: ${userEmail}`);

    const backendConfig = await fetchBackendConfiguration(userEmail, {
      maxRetries: 1,
      scope: BACKEND_CONFIG_SCOPE.bootstrap,
    });

    if (!backendConfig) {
      console.log('No backend configuration found for user');
      return true; // Not an error, just no data
    }

    console.log(
      `Loading ${Object.keys(backendConfig).length} configuration items from backend`
    );

    // Update localStorage with backend data
    Object.entries(backendConfig).forEach(([backendKey, backendValue]) => {
      const migratedEntry = migrateBackendConfigurationEntryForUser(
        backendKey,
        backendValue,
        userEmail
      );
      const { key, value } = migratedEntry;

      if (shouldSyncKey(key)) {
        try {
          const localValue = localStorage.getItem(key);

          // Special handling for languageJourney data - smart merge based on journey identity
          if (key.startsWith('languageJourney_') && localValue) {
            try {
              const localParsed = JSON.parse(localValue);
              const backendParsed =
                typeof value === 'string' ? JSON.parse(value) : value;

              const localCompletedCount =
                countJourneyCompletedActivities(localParsed);
              const backendCompletedCount =
                countJourneyCompletedActivities(backendParsed);

              // Check if they are the SAME journey (same enrollment)
              const localStartDate = localParsed.startDate
                ? new Date(localParsed.startDate).toISOString().split('T')[0]
                : null;
              const backendStartDate = backendParsed.startDate
                ? new Date(backendParsed.startDate).toISOString().split('T')[0]
                : null;
              const sameJourney =
                localStartDate === backendStartDate &&
                localParsed.currentLevel === backendParsed.currentLevel &&
                localParsed.targetLevel === backendParsed.targetLevel;

              console.log(
                `Journey merge: Local completed=${localCompletedCount}, Backend completed=${backendCompletedCount}, Same journey=${sameJourney}`
              );

              const localAdminUpdateTime = new Date(
                localParsed?.manualAdminUpdateAt || 0
              ).getTime();
              const backendAdminUpdateTime = new Date(
                backendParsed?.manualAdminUpdateAt || 0
              ).getTime();

              if (backendAdminUpdateTime > localAdminUpdateTime) {
                localStorage.setItem(key, JSON.stringify(backendParsed));
                console.log(
                  `✅ Loaded ${key} from backend (newer admin-controlled version)`
                );
                return;
              }

              if (sameJourney) {
                // SAME JOURNEY: Merge progress from both (union of completed activities)
                const merged = mergeJourneyProgressForSync(
                  localParsed,
                  backendParsed
                );
                const mergedCompletedCount =
                  countJourneyCompletedActivities(merged);
                localStorage.setItem(key, JSON.stringify(merged));
                console.log(
                  `✅ Merged ${key}: Combined progress (${mergedCompletedCount} activities)`
                );
              } else {
                // DIFFERENT JOURNEYS: Use the older one (original enrollment)
                const localStartTime = new Date(
                  localParsed.startDate || 0
                ).getTime();
                const backendStartTime = new Date(
                  backendParsed.startDate || 0
                ).getTime();

                if (backendStartTime > 0 && localStartTime > 0) {
                  // Use the OLDER one (first enrollment)
                  if (backendStartTime < localStartTime) {
                    const valueToStore =
                      typeof value === 'object' ? JSON.stringify(value) : value;
                    localStorage.setItem(key, valueToStore);
                    console.log(
                      `✅ Using backend ${key} (older/original journey)`
                    );
                  } else {
                    console.log(
                      `🔄 Keeping local ${key} (older/original journey)`
                    );
                  }
                } else {
                  // Fallback to completion count comparison
                  if (backendCompletedCount >= localCompletedCount) {
                    const valueToStore =
                      typeof value === 'object' ? JSON.stringify(value) : value;
                    localStorage.setItem(key, valueToStore);
                    console.log(
                      `✅ Loaded ${key} from backend (more progress: ${backendCompletedCount} vs ${localCompletedCount})`
                    );
                  } else {
                    console.log(
                      `🔄 Keeping local ${key} (more progress: ${localCompletedCount} vs ${backendCompletedCount})`
                    );
                  }
                }
              }
              return;
            } catch (e) {
              console.error('Error comparing journey data:', e);
              // Fall through to normal handling
            }
          }

          // Special handling for lesson_completion_data - merge (union of completed lessons)
          if (isLessonCompletionStorageKey(key) && localValue) {
            try {
              const localParsed = JSON.parse(localValue);
              const backendParsed =
                typeof value === 'string' ? JSON.parse(value) : value;

              // Merge: take union of all completed lessons
              const merged = { ...localParsed, ...backendParsed };
              localStorage.setItem(key, JSON.stringify(merged));
              console.log(
                `✅ Merged ${key}: Local had ${Object.keys(localParsed).length}, Backend had ${Object.keys(backendParsed).length}, Merged has ${Object.keys(merged).length}`
              );
              return;
            } catch (e) {
              console.error('Error merging lesson completion data:', e);
              // Fall through to normal handling
            }
          }

          // Special handling for GRE Quant/Verbal progress - keep the one with more progress
          if (
            (key.startsWith('liwaza_quant_progress_') ||
              key.startsWith('liwaza_verbal_progress_')) &&
            localValue
          ) {
            try {
              const localParsed = JSON.parse(localValue);
              const backendParsed =
                typeof value === 'string' ? JSON.parse(value) : value;

              // Count progress items for comparison
              const countProgressItems = (data) => {
                if (!data?.data) return 0;
                let count = 0;
                const progressData = data.data;
                if (progressData.overall_summary) {
                  count +=
                    progressData.overall_summary.total_study_time_minutes || 0;
                  count +=
                    (progressData.overall_summary.current_streak || 0) * 100;
                }
                if (progressData.vocabulary_mastery) {
                  count +=
                    progressData.vocabulary_mastery.total_words_encountered ||
                    0;
                  count +=
                    progressData.vocabulary_mastery.total_words_mastered || 0;
                }
                if (progressData.text_completion) {
                  count +=
                    progressData.text_completion.total_questions_attempted || 0;
                }
                if (progressData.sentence_equivalence) {
                  count +=
                    progressData.sentence_equivalence
                      .total_questions_attempted || 0;
                }
                if (progressData.reading_comprehension) {
                  count +=
                    progressData.reading_comprehension
                      .total_passages_completed || 0;
                }
                if (progressData.concept_lessons) {
                  count += progressData.concept_lessons.completed_lessons || 0;
                }
                return count;
              };

              const localProgressCount = countProgressItems(localParsed);
              const backendProgressCount = countProgressItems(backendParsed);

              console.log(
                `GRE Progress merge (${key}): Local=${localProgressCount}, Backend=${backendProgressCount}`
              );

              // Keep the one with MORE progress
              if (backendProgressCount >= localProgressCount) {
                const valueToStore =
                  typeof value === 'object' ? JSON.stringify(value) : value;
                localStorage.setItem(key, valueToStore);
                console.log(
                  `✅ Loaded ${key} from backend (more progress: ${backendProgressCount} vs ${localProgressCount})`
                );
              } else {
                console.log(
                  `🔄 Keeping local ${key} (more progress: ${localProgressCount} vs ${backendProgressCount})`
                );
              }
              return;
            } catch (e) {
              console.error('Error comparing GRE progress data:', e);
              // Fall through to normal handling
            }
          }

          // Special handling for concepts_mastery_progress - merge unique lessons
          if (
            (key === 'concepts_mastery_progress' ||
              key === 'gre_concepts_mastery_progress') &&
            localValue
          ) {
            try {
              const localParsed = JSON.parse(localValue);
              const backendParsed =
                typeof value === 'string' ? JSON.parse(value) : value;

              if (Array.isArray(localParsed) && Array.isArray(backendParsed)) {
                // Merge: union by lessonId, keeping the one with better score
                const mergedMap = new Map();

                // Add backend lessons first
                backendParsed.forEach((lesson) => {
                  mergedMap.set(lesson.lessonId || lesson.id, lesson);
                });

                // Merge local lessons, keeping better scores
                localParsed.forEach((lesson) => {
                  const id = lesson.lessonId || lesson.id;
                  const existing = mergedMap.get(id);
                  if (!existing) {
                    mergedMap.set(id, lesson);
                  } else if ((lesson.score || 0) > (existing.score || 0)) {
                    mergedMap.set(id, lesson);
                  }
                });

                const merged = Array.from(mergedMap.values());
                localStorage.setItem(key, JSON.stringify(merged));
                console.log(
                  `✅ Merged ${key}: Local had ${localParsed.length}, Backend had ${backendParsed.length}, Merged has ${merged.length}`
                );
                return;
              }
            } catch (e) {
              console.error('Error merging concepts mastery data:', e);
              // Fall through to normal handling
            }
          }

          // Special handling for GRE vocab article progress - merge
          if (key.startsWith('gre_vocab_article_progress_') && localValue) {
            try {
              const localParsed = JSON.parse(localValue);
              const backendParsed =
                typeof value === 'string' ? JSON.parse(value) : value;

              // Merge: take union of all article progress
              const merged = { ...localParsed, ...backendParsed };
              localStorage.setItem(key, JSON.stringify(merged));
              console.log(
                `✅ Merged ${key}: Local had ${Object.keys(localParsed).length}, Backend had ${Object.keys(backendParsed).length}, Merged has ${Object.keys(merged).length}`
              );
              return;
            } catch (e) {
              console.error('Error merging vocab article progress:', e);
              // Fall through to normal handling
            }
          }

          // Special handling for voice_minutes_data - keep the one with more usage
          if (key === 'voice_minutes_data' && localValue) {
            try {
              const localParsed = JSON.parse(localValue);
              const backendParsed =
                typeof value === 'string' ? JSON.parse(value) : value;

              const localMinutes = localParsed?.totalMinutesUsed || 0;
              const backendMinutes = backendParsed?.totalMinutesUsed || 0;

              if (backendMinutes >= localMinutes) {
                const valueToStore =
                  typeof value === 'object' ? JSON.stringify(value) : value;
                localStorage.setItem(key, valueToStore);
                console.log(
                  `✅ Loaded ${key} from backend (more usage: ${backendMinutes}min vs ${localMinutes}min)`
                );
              } else {
                console.log(
                  `🔄 Keeping local ${key} (more usage: ${localMinutes}min vs ${backendMinutes}min)`
                );
              }
              return;
            } catch (e) {
              console.error('Error comparing voice minutes:', e);
            }
          }

          // Special handling for training plan - keep the one with more completed activities
          if (key === 'liwaza-training-plan' && localValue) {
            try {
              const localParsed = JSON.parse(localValue);
              const backendParsed =
                typeof value === 'string' ? JSON.parse(value) : value;

              const countCompleted = (plan) => {
                if (!plan) return 0;
                let count = 0;
                if (plan.studyActivities)
                  count += plan.studyActivities.filter(
                    (a) => a.completed
                  ).length;
                if (plan.weeklyGoals)
                  count += plan.weeklyGoals.filter((g) => g.completed).length;
                return count;
              };

              const localCompleted = countCompleted(localParsed);
              const backendCompleted = countCompleted(backendParsed);

              if (backendCompleted >= localCompleted) {
                const valueToStore =
                  typeof value === 'object' ? JSON.stringify(value) : value;
                localStorage.setItem(key, valueToStore);
                console.log(
                  `✅ Loaded ${key} from backend (more completed: ${backendCompleted} vs ${localCompleted})`
                );
              } else {
                console.log(
                  `🔄 Keeping local ${key} (more completed: ${localCompleted} vs ${backendCompleted})`
                );
              }
              return;
            } catch (e) {
              console.error('Error comparing training plan:', e);
            }
          }

          // Special handling for applications - merge by school name
          if (key === 'liwaza-applications' && localValue) {
            try {
              const localParsed = JSON.parse(localValue);
              const backendParsed =
                typeof value === 'string' ? JSON.parse(value) : value;

              if (Array.isArray(localParsed) && Array.isArray(backendParsed)) {
                // Merge by school name, keeping the more complete version
                const mergedMap = new Map();

                backendParsed.forEach((app) => {
                  mergedMap.set(app.schoolName || app.school, app);
                });

                localParsed.forEach((app) => {
                  const id = app.schoolName || app.school;
                  const existing = mergedMap.get(id);
                  if (!existing) {
                    mergedMap.set(id, app);
                  }
                  // Keep backend version if it exists (more likely to be up to date)
                });

                const merged = Array.from(mergedMap.values());
                localStorage.setItem(key, JSON.stringify(merged));
                console.log(
                  `✅ Merged ${key}: Local had ${localParsed.length}, Backend had ${backendParsed.length}, Merged has ${merged.length}`
                );
                return;
              }
            } catch (e) {
              console.error('Error merging applications:', e);
            }
          }

          // Special handling for onboarding tasks - keep more completed
          if (key === 'onboardingTasks' && localValue) {
            try {
              const localParsed = JSON.parse(localValue);
              const backendParsed =
                typeof value === 'string' ? JSON.parse(value) : value;

              if (Array.isArray(localParsed) && Array.isArray(backendParsed)) {
                const localCompleted = localParsed.filter(
                  (t) => t.completed
                ).length;
                const backendCompleted = backendParsed.filter(
                  (t) => t.completed
                ).length;

                if (backendCompleted >= localCompleted) {
                  const valueToStore =
                    typeof value === 'object' ? JSON.stringify(value) : value;
                  localStorage.setItem(key, valueToStore);
                  console.log(
                    `✅ Loaded ${key} from backend (more completed: ${backendCompleted} vs ${localCompleted})`
                  );
                } else {
                  console.log(
                    `🔄 Keeping local ${key} (more completed: ${localCompleted} vs ${backendCompleted})`
                  );
                }
                return;
              }
            } catch (e) {
              console.error('Error comparing onboarding tasks:', e);
            }
          }

          // Special handling for copilot conversations - keep more messages
          if (key.startsWith('copilot_conversation_') && localValue) {
            try {
              const localParsed = JSON.parse(localValue);
              const backendParsed =
                typeof value === 'string' ? JSON.parse(value) : value;

              const getMessageCount = (data) => {
                if (Array.isArray(data)) return data.length;
                if (data?.messages && Array.isArray(data.messages))
                  return data.messages.length;
                return 0;
              };

              const localMessages = getMessageCount(localParsed);
              const backendMessages = getMessageCount(backendParsed);

              if (backendMessages >= localMessages) {
                const valueToStore =
                  typeof value === 'object' ? JSON.stringify(value) : value;
                localStorage.setItem(key, valueToStore);
                console.log(
                  `✅ Loaded ${key} from backend (more messages: ${backendMessages} vs ${localMessages})`
                );
              } else {
                console.log(
                  `🔄 Keeping local ${key} (more messages: ${localMessages} vs ${backendMessages})`
                );
              }
              return;
            } catch (e) {
              console.error('Error comparing copilot conversation:', e);
            }
          }

          // Special handling for saved concepts - merge unique
          if (key === 'savedConcepts' && localValue) {
            try {
              const localParsed = JSON.parse(localValue);
              const backendParsed =
                typeof value === 'string' ? JSON.parse(value) : value;

              if (Array.isArray(localParsed) && Array.isArray(backendParsed)) {
                // Merge unique concepts by id
                const mergedMap = new Map();
                backendParsed.forEach((c) => mergedMap.set(c.id || c.name, c));
                localParsed.forEach((c) => {
                  if (!mergedMap.has(c.id || c.name)) {
                    mergedMap.set(c.id || c.name, c);
                  }
                });

                const merged = Array.from(mergedMap.values());
                localStorage.setItem(key, JSON.stringify(merged));
                console.log(
                  `✅ Merged ${key}: Local had ${localParsed.length}, Backend had ${backendParsed.length}, Merged has ${merged.length}`
                );
                return;
              }
            } catch (e) {
              console.error('Error merging saved concepts:', e);
            }
          }

          // Preserve the richer local cache when backend stores a compact
          // cross-device summary of speaking/test history.
          if (isUserProgressStorageKey(key) && localValue) {
            try {
              const localParsed = JSON.parse(localValue);
              const backendParsed =
                typeof value === 'string' ? JSON.parse(value) : value;

              if (
                Array.isArray(localParsed) &&
                Array.isArray(backendParsed) &&
                (localParsed.length > backendParsed.length ||
                  (localParsed.length === backendParsed.length &&
                    localValue.length > JSON.stringify(backendParsed).length))
              ) {
                console.log(
                  `Keeping local ${key} (richer local history cache than backend summary)`
                );
                return;
              }
            } catch (e) {
              console.error('Error comparing userProgress payloads:', e);
            }
          }

          // Default behavior: if overwriteLocal is false, skip if key exists
          if (!overwriteLocal && localValue !== null) {
            console.log(`Skipping ${key} - already exists locally`);
            return;
          }

          // Store as JSON string if it's an object, otherwise as string
          const valueToStore =
            typeof value === 'object' ? JSON.stringify(value) : value;
          localStorage.setItem(key, valueToStore);
          console.log(`Loaded ${key} from backend`);
        } catch (error) {
          console.warn(`Error setting localStorage key ${key}:`, error);
        }
      }
    });

    // Store last load timestamp
    localStorage.setItem('_last_backend_load', Date.now().toString());

    console.log('Backend configuration successfully loaded to localStorage');
    return true;
  } catch (error) {
    console.error(
      'Error loading backend configuration to localStorage:',
      error
    );
    return false;
  }
};

/**
 * Check if a key is critical and should sync immediately (no debounce)
 * Critical keys are those that would cause data loss if not synced immediately
 * @param {string} key - localStorage key
 * @returns {boolean} - true if critical
 */
const isCriticalSyncKey = (key) => {
  // Journey data is critical - must sync immediately
  if (key.startsWith('languageJourney_')) return true;
  // Lesson completion is critical
  if (isLessonCompletionStorageKey(key)) return true;
  // User progress is critical
  if (isUserProgressStorageKey(key)) return true;
  // GRE progress is critical
  if (key.startsWith('liwaza_quant_progress_')) return true;
  if (key.startsWith('liwaza_verbal_progress_')) return true;
  return false;
};

/**
 * Enhanced localStorage.setItem that automatically syncs to backend
 * Use this instead of direct localStorage.setItem for sync-worthy data
 * @param {string} key - localStorage key
 * @param {string} value - value to store
 * @param {boolean} skipSync - skip backend sync (for cases where sync is already handled)
 * @returns {Promise<void>}
 */
export const setLocalStorageWithSync = async (key, value, skipSync = false) => {
  try {
    // Set in localStorage first
    localStorage.setItem(key, value);

    // Sync to backend if key should be synced and sync is not skipped
    if (!skipSync && shouldSyncKey(key)) {
      const user =
        getStoredUser() || JSON.parse(localStorage.getItem('user') || '{}');
      const email = user?.email || localStorage.getItem('userEmail');

      if (email) {
        // CRITICAL keys sync immediately (no debounce) to prevent data loss
        if (isCriticalSyncKey(key)) {
          // Coalesce multiple critical writes (common during login/bootstrap).
          // Still "immediate" for UX, but avoids duplicate sync loops.
          window._criticalSyncTimeouts = window._criticalSyncTimeouts || {};
          const timeoutKey = String(email).toLowerCase();
          clearTimeout(window._criticalSyncTimeouts[timeoutKey]);
          window._criticalSyncTimeouts[timeoutKey] = setTimeout(() => {
            console.log(`🔄 Immediate sync for critical key: ${key}`);
            syncLocalStorageToBackend(email, true).catch((err) => {
              console.error(`Failed to sync critical key ${key}:`, err);
            });
          }, 200);
        } else {
          // Non-critical keys use debounce to reduce API calls
          clearTimeout(window._syncTimeout);
          window._syncTimeout = setTimeout(async () => {
            await syncLocalStorageToBackend(email);
          }, 1000); // Wait 1 second before syncing
        }
      }
    }
  } catch (error) {
    console.error('Error in setLocalStorageWithSync:', error);
    // Still set in localStorage even if sync fails
    localStorage.setItem(key, value);
  }
};

/**
 * Clear all syncable localStorage data (for logout)
 */
export const clearSyncableLocalStorage = () => {
  console.log('Clearing account-specific localStorage data');

  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (shouldSyncKey(key) || isUnsafeAccountStorageKey(key))) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => {
    localStorage.removeItem(key);
    console.log(`Removed localStorage key: ${key}`);
  });

  // Also remove sync timestamps
  localStorage.removeItem('_last_backend_sync');
  localStorage.removeItem('_last_backend_load');

  console.log(`Cleared ${keysToRemove.length} account-specific localStorage items`);
};

/**
 * Get sync status information
 * @returns {object} - sync status details
 */
export const getSyncStatus = () => {
  const lastSync = localStorage.getItem('_last_backend_sync');
  const lastLoad = localStorage.getItem('_last_backend_load');
  const syncableKeys = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && shouldSyncKey(key)) {
      syncableKeys.push(key);
    }
  }

  return {
    lastSyncTime: lastSync ? new Date(parseInt(lastSync)) : null,
    lastLoadTime: lastLoad ? new Date(parseInt(lastLoad)) : null,
    syncableKeysCount: syncableKeys.length,
    syncableKeys: syncableKeys,
  };
};
